<template>
  <div class="column">
    <!-- INITIAL MODAL -->
    <div v-if="showInitialModal" class="initial-modal">
      <div class="initial-modal-content">
        <button
          class="close-button"
          :title="$t('AUTOMATION.CLOSE')"
          @click="onClose"
        >
          ✕
        </button>
        <h2 class="title">
          {{ $t('AUTOMATION.ADD.INITIAL_MODAL.TITLE') }}
        </h2>

        <div class="options">
          <div class="option primary" @click="startFromScratch">
            <div class="icon">
              <fluent-icon icon="file-upload" size="28" />
            </div>

            <span>
              {{ $t('AUTOMATION.ADD.INITIAL_MODAL.START_FROM_SCRATCH') }}
            </span>
          </div>

          <div class="option" @click="openTemplates">
            <div class="icon">
              <fluent-icon icon="folder" size="28" />
            </div>

            <span>
              {{ $t('AUTOMATION.ADD.INITIAL_MODAL.USE_EXAMPLES') }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showCategoryModal" class="initial-modal">
      <div class="initial-modal-content trigger-category-modal">
        <button
          class="back-button trigger-category-modal__back"
          :title="$t('AUTOMATION.ADD.INITIAL_MODAL.BACK')"
          @click="backToInitial"
        >
          ←
        </button>
        <button
          class="close-button"
          :title="$t('AUTOMATION.CLOSE')"
          @click="onClose"
        >
          ✕
        </button>
        <h2 class="trigger-category-modal__title">
          {{ $t('AUTOMATION.ADD.TRIGGER_CATEGORY.TITLE') }}
        </h2>

        <div class="trigger-category-modal__options" role="radiogroup">
          <button
            v-for="category in availableTriggerCategories"
            :key="category.key"
            type="button"
            role="radio"
            :aria-checked="String(category.key === triggerCategory)"
            :class="[
              'trigger-category-option',
              {
                'trigger-category-option--selected':
                  category.key === triggerCategory,
              },
            ]"
            @click="triggerCategory = category.key"
          >
            <fluent-icon :icon="category.icon" size="48" />
            <span>
              {{
                $t(
                  `AUTOMATION.TRIGGER_CATEGORIES.${category.key.toUpperCase()}`
                )
              }}
            </span>
          </button>
        </div>

        <div class="trigger-category-modal__footer">
          <woot-button @click="startWithTriggerCategory">
            {{ $t('AUTOMATION.ADD.TRIGGER_CATEGORY.START') }}
          </woot-button>
        </div>
      </div>
    </div>

    <!-- TEMPLATES LIST -->
    <div v-if="showTemplates" class="templates-modal">
      <div class="templates-content">
        <div class="templates-header">
          <button
            class="back-button"
            :title="$t('AUTOMATION.ADD.INITIAL_MODAL.BACK')"
            @click="backToInitial"
          >
            ←
          </button>

          <h3>
            {{ $t('AUTOMATION.ADD.INITIAL_MODAL.SELECT_TEMPLATE') }}
          </h3>

          <button
            class="close-button"
            :title="$t('AUTOMATION.CLOSE')"
            @click="onClose"
          >
            ✕
          </button>
        </div>

        <div class="templates-list">
          <div
            v-for="template in automationTemplates"
            :key="template.id"
            class="template-card"
            @click="applyTemplate(template)"
          >
            <div class="template-info">
              <div class="template-title">
                {{ template.title }}
              </div>

              <div class="template-description">
                {{ template.description }}
              </div>
            </div>

            <div class="template-action">→</div>
          </div>
        </div>
      </div>
    </div>

    <!-- MAIN FORM -->
    <div v-if="!showInitialModal && !showCategoryModal && !showTemplates">
      <woot-modal-header :header-title="$t('AUTOMATION.ADD.TITLE')" />

      <div class="steps-indicator">
        <div :class="['step', { active: currentStep === 1 }]">1</div>

        <div class="line"></div>

        <div :class="['step', { active: currentStep === 2 }]">2</div>

        <div class="line"></div>

        <div :class="['step', { active: currentStep === 3 }]">3</div>
      </div>

      <div class="row modal-content">
        <div class="medium-12 columns">
          <template v-if="currentStep === 1">
            <woot-input
              v-model="automation.name"
              :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
              type="text"
              :class="{ error: $v.automation.name.$error }"
              :error="
                $v.automation.name.$error
                  ? $t('AUTOMATION.ADD.FORM.NAME.ERROR')
                  : ''
              "
              :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
              @blur="$v.automation.name.$touch"
            />

            <woot-input
              v-model="automation.description"
              :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
              type="text"
              :class="{ error: $v.automation.description.$error }"
              :error="
                $v.automation.description.$error
                  ? $t('AUTOMATION.ADD.FORM.DESC.ERROR')
                  : ''
              "
              :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
              @blur="$v.automation.description.$touch"
            />

            <div class="event_wrapper">
              <label :class="{ error: $v.automation.event_name.$error }">
                {{ $t('AUTOMATION.ADD.FORM.EVENT.LABEL') }}

                <select
                  v-model="automation.event_name"
                  @change="onEventChange()"
                >
                  <option
                    v-for="event in triggerCategoryEvents"
                    :key="event.key"
                    :value="event.key"
                  >
                    {{
                      $t(
                        `INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.EVENTS.${event.attributeI18nKey}`
                      )
                    }}
                  </option>
                </select>

                <span v-if="$v.automation.event_name.$error" class="message">
                  {{
                    $v.automation.event_name.belongsToTriggerCategory
                      ? $t('AUTOMATION.ADD.FORM.EVENT.ERROR')
                      : $t('AUTOMATION.ADD.FORM.EVENT.CATEGORY_ERROR')
                  }}
                </span>
              </label>

              <p v-if="hasAutomationMutated" class="info-message">
                {{ $t('AUTOMATION.FORM.RESET_MESSAGE') }}
              </p>
            </div>
          </template>

          <template v-if="currentStep === 2">
            <section>
              <label>
                {{ $t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL') }}
              </label>

              <div class="medium-12 columns filters-wrap">
                <filter-input-box
                  v-for="(condition, i) in automation.conditions"
                  :key="i"
                  v-model="automation.conditions[i]"
                  :filter-attributes="getAttributes(automation.event_name)"
                  :input-type="
                    getInputType(automation.conditions[i].attribute_key)
                  "
                  :operators="
                    getOperators(automation.conditions[i].attribute_key)
                  "
                  :dropdown-values="
                    getConditionDropdownValues(
                      automation.conditions[i].attribute_key
                    )
                  "
                  :show-query-operator="i !== automation.conditions.length - 1"
                  :custom-attribute-type="
                    getCustomAttributeType(
                      automation.conditions[i].attribute_key
                    )
                  "
                  :v="$v.automation.conditions.$each[i]"
                  @resetFilter="resetFilter(i, automation.conditions[i])"
                  @removeFilter="removeFilter(i)"
                />

                <div class="filter-actions">
                  <woot-button
                    icon="add"
                    color-scheme="success"
                    variant="smooth"
                    size="small"
                    @click="appendNewCondition"
                  >
                    {{ $t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL') }}
                  </woot-button>
                </div>
              </div>
            </section>
          </template>

          <template v-if="currentStep === 3">
            <section>
              <label>
                {{ $t('AUTOMATION.ADD.FORM.ACTIONS.LABEL') }}
              </label>

              <div class="medium-12 columns filters-wrap">
                <automation-action-input
                  v-for="(action, i) in automation.actions"
                  :key="i"
                  v-model="automation.actions[i]"
                  :action-types="
                    getSelectableActionTypes(automation.actions[i].action_name)
                  "
                  :dropdown-values="
                    getActionDropdownValues(automation.actions[i].action_name)
                  "
                  :show-action-input="
                    showActionInput(automation.actions[i].action_name)
                  "
                  :v="$v.automation.actions.$each[i]"
                  @resetAction="resetAction(i)"
                  @removeAction="removeAction(i)"
                />

                <div class="filter-actions">
                  <woot-button
                    icon="add"
                    color-scheme="success"
                    variant="smooth"
                    size="small"
                    @click="appendNewAction"
                  >
                    {{ $t('AUTOMATION.ADD.ACTION_BUTTON_LABEL') }}
                  </woot-button>
                </div>
              </div>
            </section>
          </template>

          <div class="medium-12 columns">
            <div class="modal-footer justify-content-end w-full">
              <woot-button
                v-if="currentStep > 1"
                class="button clear"
                @click.prevent="prevStep"
              >
                {{ $t('AUTOMATION.ADD.INITIAL_MODAL.PREVIOUS') }}
              </woot-button>

              <woot-button v-if="currentStep < 3" @click="nextStep">
                {{ $t('AUTOMATION.ADD.INITIAL_MODAL.NEXT') }}
              </woot-button>

              <woot-button v-if="currentStep === 3" @click="submitAutomation">
                {{ $t('AUTOMATION.ADD.SUBMIT') }}
              </woot-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import automationMethodsMixin from 'dashboard/mixins/automations/methodsMixin';
import automationValidationsMixin from 'dashboard/mixins/automations/validationsMixin';
import filterInputBox from 'dashboard/components/widgets/FilterInput/Index.vue';
import automationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import {
  AUTOMATION_RULE_EVENTS,
  AUTOMATION_ACTION_TYPES,
  AUTOMATIONS,
} from './constants';
import automationTemplates from './automationTemplates.json';
import {
  AUTOMATION_TRIGGER_CATEGORIES,
  getAutomationTriggerCategory,
} from './constants/events';

export default {
  components: {
    filterInputBox,
    automationActionInput,
  },
  mixins: [alertMixin, automationMethodsMixin, automationValidationsMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    initialCategory: {
      type: String,
      default: AUTOMATION_TRIGGER_CATEGORIES[0].key,
    },
  },
  data() {
    return {
      showInitialModal: true,
      showCategoryModal: false,
      triggerCategory: this.initialCategory,
      showTemplates: false,
      automationTemplates,
      currentStep: 1,
      automationTypes: JSON.parse(JSON.stringify(AUTOMATIONS)),
      automationRuleEvent: AUTOMATION_RULE_EVENTS[0].key,
      automationRuleEvents: AUTOMATION_RULE_EVENTS,
      automationActionTypes: AUTOMATION_ACTION_TYPES,
      automationMutated: false,
      show: true,
      automation: {
        name: null,
        description: null,
        event_name: 'conversation_created',
        conditions: [
          {
            attribute_key: 'status',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
            custom_attribute_type: '',
          },
        ],
        actions: [
          {
            action_name: 'assign_agent',
            action_params: [],
          },
        ],
      },
      allCustomAttributes: [],
    };
  },
  computed: {
    availableTriggerCategories() {
      const accountId = this.$store.getters.getCurrentAccountId;
      return AUTOMATION_TRIGGER_CATEGORIES.filter(
        category =>
          !category.featureFlag ||
          this.$store.getters['accounts/isFeatureEnabledonAccount'](
            accountId,
            category.featureFlag
          )
      );
    },
    triggerCategoryEvents() {
      return this.sortedAutomationRuleEvents.filter(
        event => event.category === this.triggerCategory
      );
    },
    hasAutomationMutated() {
      return (
        this.automation.conditions[0].values ||
        this.automation.actions[0].action_params.length
      );
    },
    sortedAutomationRuleEvents() {
      return [...this.automationRuleEvents].sort((a, b) => {
        const labelA = this.$t(
          `INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.EVENTS.${a.attributeI18nKey}`
        );
        const labelB = this.$t(
          `INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.EVENTS.${b.attributeI18nKey}`
        );
        return labelA.localeCompare(labelB, 'pt-BR', { sensitivity: 'base' });
      });
    },
  },
  methods: {
    startFromScratch() {
      const isAvailable = this.availableTriggerCategories.some(
        category => category.key === this.triggerCategory
      );
      if (!isAvailable) {
        this.triggerCategory = this.availableTriggerCategories[0].key;
      }
      this.showInitialModal = false;
      this.showTemplates = false;
      this.showCategoryModal = true;
    },
    startWithTriggerCategory() {
      const category = AUTOMATION_TRIGGER_CATEGORIES.find(
        ({ key }) => key === this.triggerCategory
      );
      this.automation.event_name = category.defaultEvent;
      this.onEventChange();
      this.showCategoryModal = false;
      this.currentStep = 1;
    },
    openTemplates() {
      this.showInitialModal = false;
      this.showTemplates = true;
    },
    backToInitial() {
      this.showInitialModal = true;
      this.showCategoryModal = false;
      this.showTemplates = false;
    },
    applyTemplate(template) {
      const statusLabelMap = {
        open: 'Abertas',
        resolved: 'Resolvida',
        pending: 'Com robô',
        snoozed: 'Adiadas',
      };

      this.automation = {
        name: template.name,
        description: template.description,
        event_name: template.event_name,
        conditions: template.conditions.map(condition => {
          let formattedValue = condition.values;

          if (
            condition.attribute_key === 'status' &&
            typeof condition.values === 'string'
          ) {
            formattedValue = [
              {
                id: condition.values,
                name: statusLabelMap[condition.values] || condition.values,
              },
            ];
          }

          return {
            ...condition,
            values: formattedValue,
            custom_attribute_type: '',
          };
        }),
        actions: template.actions.map(action => ({
          ...action,
        })),
      };

      this.triggerCategory = getAutomationTriggerCategory(template.event_name);
      this.showTemplates = false;
      this.currentStep = 1;
    },
    nextStep() {
      if (this.currentStep === 1) {
        this.$v.automation.name.$touch();
        this.$v.automation.description.$touch();

        if (
          this.$v.automation.name.$invalid ||
          this.$v.automation.description.$invalid
        ) {
          return;
        }
      }

      if (this.currentStep < 3) this.currentStep++;
    },
    prevStep() {
      if (this.currentStep > 1) this.currentStep--;
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('agents/get');
    this.$store.dispatch('contacts/get');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('labels/get');
    this.$store.dispatch('macros/get');
    this.$store.dispatch('campaigns/get');
    this.$store.dispatch('closingReasons/get', { page: 1, limit: 100 });
    this.$store.dispatch('events/get');
    if (
      this.$store.getters['accounts/isFeatureEnabledonAccount'](
        this.$store.getters.getCurrentAccountId,
        'opportunity_pipelines'
      )
    ) {
      this.$store.dispatch('opportunityPipelines/get');
    }
    this.$store.dispatch('campaigns/getCampaignWhatsapp', {
      page: 1,
      sortAttr: '-created_at',
      limit: 200,
      combo_select: true,
    });

    this.allCustomAttributes = this.$store.getters['attributes/getAttributes'];

    this.manifestCustomAttributes();
  },
};
</script>

<style scoped>
.initial-modal,
.templates-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.initial-modal-content,
.templates-content {
  position: relative;
  background: #fff;
  border-radius: 12px;
}

.initial-modal-content {
  padding: 40px;
  width: 600px;
  text-align: center;
}

.templates-content {
  padding: 24px;
  width: 720px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  border-radius: 8px;
  color: #6b7280;
  transition: all 0.15s ease;
}

.close-button:hover {
  background: #f3f4f6;
  color: #111827;
}

.templates-header {
  display: grid;
  grid-template-columns: 32px 1fr 32px;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.templates-header h3 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  text-align: center;
}

.templates-list {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 4px;
}

.template-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  cursor: pointer;
  transition: all 0.2s ease;
}

.template-card:hover {
  background: #eef2ff;
  border-color: #c7d2fe;
  transform: translateY(-1px);
}

.template-info {
  display: flex;
  flex-direction: column;
}

.template-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.template-description {
  font-size: 13px;
  color: #6b7280;
}

.template-action {
  font-size: 18px;
  color: #9ca3af;
}

.back-button {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 8px;

  display: flex;
  align-items: center;
  justify-content: center;
}

.back-button:hover {
  background: #f3f4f6;
}

.title {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 30px;
}

.options {
  display: flex;
  gap: 20px;
  justify-content: center;
}

.option {
  width: 200px;
  height: 140px;
  border-radius: 12px;
  background: #e5e7eb;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.option:hover {
  transform: translateY(-2px);
}

.option.primary {
  background: #2563eb;
  color: #fff;
}

.icon {
  font-size: 28px;
}

.trigger-category-modal {
  padding: var(--space-large) var(--space-larger);
  text-align: left;
  width: 66rem;
}

.trigger-category-modal__back {
  left: var(--space-normal);
  position: absolute;
  top: var(--space-normal);
}

.trigger-category-modal__title {
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-bold);
  margin: var(--space-normal) 0 var(--space-large);
}

.trigger-category-modal__options {
  display: grid;
  gap: var(--space-medium);
  grid-template-columns: repeat(3, 1fr);
}

.trigger-category-option {
  align-items: center;
  aspect-ratio: 1;
  background: var(--s-50);
  border-radius: var(--border-radius-large);
  color: var(--s-500);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  font-size: var(--font-size-default);
  gap: var(--space-normal);
  justify-content: center;
  padding: var(--space-normal);
  text-align: center;
  transition: background 0.15s ease, color 0.15s ease;
}

.trigger-category-option:hover {
  background: var(--s-75);
}

.trigger-category-option--selected,
.trigger-category-option--selected:hover {
  background: var(--c-500);
  color: var(--white);
  font-weight: var(--font-weight-bold);
}

.trigger-category-modal__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: var(--space-large);
}

.steps-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 18px;
  margin-bottom: 28px;
}

.step {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #e5e5e5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
}

.step.active {
  background: #2563eb;
  color: white;
}

.line {
  width: 50px;
  height: 2px;
  background: #e5e5e5;
}
</style>
